import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Clock, Cpu, Loader2, AlertCircle, Activity, RotateCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

// Define task type for TypeScript
interface Task {
  id: string;
  type: string;
  status: string;  // 'pending', 'running', 'done', 'failed'
  created_at: string;
  updated_at: string;
  slug: string;  // This is the company slug in the database
  result?: any;
  // Additional fields that might be in the database
  job_id?: string;
  params?: any;
  version?: string;
  quarter?: string;
  force?: boolean;
  dependencies?: string[];
}

interface TaskStatusIndicatorProps {
  slug: string;
  className?: string;
}

const TaskStatusIndicator: React.FC<TaskStatusIndicatorProps> = ({ slug, className = '' }) => {
  // State for tasks and modal
  const [activeTasks, setActiveTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  
  // Reference to store the interval ID for cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to fetch tasks from Supabase
  const fetchTasks = async () => {
    try {
      if (!slug) {
        console.log('No slug provided to TaskStatusIndicator');
        return;
      }
      
      console.log(`🔍 [TaskStatusIndicator] Fetching tasks for slug: ${slug}`);
      
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('slug', slug)
        .in('status', ['pending', 'running'])
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching tasks:', error);
        setError('Failed to fetch tasks');
        return;
      }
      
      console.log(`✅ [TaskStatusIndicator] Found ${data?.length || 0} active tasks for ${slug}`);
      setActiveTasks(data || []);
      setError(null);
    } catch (err) {
      console.error('Exception in fetchTasks:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Set up polling when component mounts
  useEffect(() => {
    // Fetch tasks immediately
    fetchTasks();
    
    // Set up polling every 15 seconds
    pollingIntervalRef.current = setInterval(fetchTasks, 15000);
    
    // Clean up the interval when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [slug]);

  // Get the appropriate icon based on task status
  const getStatusIcon = () => {
    if (isLoading) {
      return <Loader2 className="h-5 w-5 animate-spin text-gray-500" />;
    }
    
    if (error) {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
    
    if (activeTasks.length === 0) {
      return null; // No icon when no active tasks
    }
    
    // Check if any tasks are running
    const hasRunningTasks = activeTasks.some(task => task.status === 'running');
    
    if (hasRunningTasks) {
      return <RotateCw className="h-5 w-5 text-blue-500 animate-spin" />;
    }
    
    // Only pending tasks
    return <Clock className="h-5 w-5 text-amber-500" />;
  };

  // Get tooltip text based on task status
  const getTooltipText = () => {
    if (isLoading) {
      return 'Checking task status...';
    }
    
    if (error) {
      return `Error checking tasks: ${error}`;
    }
    
    if (activeTasks.length === 0) {
      return 'No active tasks';
    }
    
    const runningCount = activeTasks.filter(task => task.status === 'running').length;
    const pendingCount = activeTasks.filter(task => task.status === 'pending').length;
    
    let tooltipText = '';
    
    if (runningCount > 0) {
      tooltipText += `${runningCount} running task${runningCount > 1 ? 's' : ''}`;
    }
    
    if (pendingCount > 0) {
      if (tooltipText) tooltipText += ', ';
      tooltipText += `${pendingCount} pending task${pendingCount > 1 ? 's' : ''}`;
    }
    
    return tooltipText + '. Click to view details.';
  };

  // Format the task type for display
  const formatTaskType = (type: string) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get the duration since the task was created
  const getTaskDuration = (task: Task) => {
    const createdAt = new Date(task.created_at);
    const now = new Date();
    const diffMs = now.getTime() - createdAt.getTime();
    
    // If less than a minute, show seconds
    if (diffMs < 60000) {
      return `${Math.floor(diffMs / 1000)}s`;
    }
    
    // If less than an hour, show minutes
    if (diffMs < 3600000) {
      return `${Math.floor(diffMs / 60000)}m`;
    }
    
    // Otherwise show hours
    return `${Math.floor(diffMs / 3600000)}h ${Math.floor((diffMs % 3600000) / 60000)}m`;
  };

  // Render status badge for task
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-blue-500">Running</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500">Pending</Badge>;
      case 'done':
        return <Badge className="bg-green-500">Done</Badge>;
      case 'failed':
        return <Badge className="bg-red-500">Failed</Badge>;
      default:
        return <Badge className="bg-gray-500">{status}</Badge>;
    }
  };

  // If there are no active tasks and no errors, don't render anything
  if (!isLoading && !error && activeTasks.length === 0) {
    return null;
  }

  return (
    <div className={`inline-flex items-center ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="p-1" 
              onClick={() => setIsModalOpen(true)}
              disabled={activeTasks.length === 0 && !error}
            >
              {getStatusIcon()}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{getTooltipText()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Active Tasks for {slug}</DialogTitle>
            <DialogDescription>
              {activeTasks.length === 0 
                ? 'No active tasks at the moment' 
                : `${activeTasks.length} active task${activeTasks.length > 1 ? 's' : ''}`}
            </DialogDescription>
          </DialogHeader>
          
          {activeTasks.length > 0 && (
            <ScrollArea className="max-h-[400px] mt-4">
              <div className="space-y-4">
                {activeTasks.map(task => (
                  <div key={task.id} className="border rounded-lg p-3 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium">{formatTaskType(task.type)}</div>
                      {renderStatusBadge(task.status)}
                    </div>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>Created: {new Date(task.created_at).toLocaleString()}</div>
                      <div>Duration: {getTaskDuration(task)}</div>
                      {task.params && (
                        <div className="mt-2">
                          <div className="font-medium text-xs text-gray-700 mb-1">Parameters:</div>
                          <pre className="text-xs bg-gray-50 p-2 rounded-md overflow-x-auto">
                            {JSON.stringify(task.params, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchTasks()}
            >
              Refresh
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => setIsModalOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskStatusIndicator;
